// frontend/src/modules/admin/components/MemoEntitesHTML.jsx
import React, { useState } from 'react';

const MemoEntitesHTML = () => {
  const [copied, setCopied] = useState('');

  // Tableau des entités HTML courantes
  const entites = [
    { categorie: 'Voyelles accentuées', items: [
      { caractere: 'é', code: '&eacute;', exemple: 'café, été' },
      { caractere: 'è', code: '&egrave;', exemple: 'très, après' },
      { caractere: 'ê', code: '&ecirc;', exemple: 'être, prêt' },
      { caractere: 'à', code: '&agrave;', exemple: 'à, déjà' },
      { caractere: 'â', code: '&acirc;', exemple: 'âge, pâte' },
      { caractere: 'ô', code: '&ocirc;', exemple: 'côte, hôtel' },
      { caractere: 'ù', code: '&ugrave;', exemple: 'où' },
      { caractere: 'û', code: '&ucirc;', exemple: 'sûr, flûte' },
      { caractere: 'î', code: '&icirc;', exemple: 'île, maître' },
      { caractere: 'ï', code: '&iuml;', exemple: 'maïs, naïf' },
      { caractere: 'ë', code: '&euml;', exemple: 'Noël' },
      { caractere: 'ü', code: '&uuml;', exemple: 'aigüe' }
    ]},
    { categorie: 'Majuscules accentuées', items: [
      { caractere: 'É', code: '&Eacute;', exemple: 'État, Église' },
      { caractere: 'È', code: '&Egrave;', exemple: 'Ère' },
      { caractere: 'À', code: '&Agrave;', exemple: 'À propos' },
      { caractere: 'Ç', code: '&Ccedil;', exemple: 'Ça' }
    ]},
    { categorie: 'Autres caractères', items: [
      { caractere: 'ç', code: '&ccedil;', exemple: 'français, reçu' },
      { caractere: 'œ', code: '&oelig;', exemple: 'cœur, bœuf' },
      { caractere: 'æ', code: '&aelig;', exemple: 'ex æquo' }
    ]},
    { categorie: 'Symboles courants', items: [
      { caractere: '€', code: '&euro;', exemple: '10€, prix' },
      { caractere: '°', code: '&deg;', exemple: 'n°, 20°C' },
      { caractere: '&', code: '&amp;', exemple: 'vous & moi' },
      { caractere: '<', code: '&lt;', exemple: 'prix < 10' },
      { caractere: '>', code: '&gt;', exemple: 'prix > 5' },
      { caractere: '"', code: '&quot;', exemple: 'citation "test"' },
      { caractere: '\'', code: '&apos;', exemple: 'aujourd\'hui' },
      { caractere: ' ', code: '&nbsp;', exemple: 'espace insécable' }
    ]}
  ];

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">📚 Mémo des Entités HTML</h2>
        <p className="text-blue-100">
          Pour modifier les templates email avec les bons accents français
        </p>
      </div>

      {/* Info importante */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <span className="text-2xl">💡</span>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-800">
              <strong>Pourquoi utiliser ces codes ?</strong><br/>
              Dans le HTML des emails, les accents peuvent causer des problèmes d'affichage.
              Les entités HTML garantissent que les caractères s'affichent correctement partout !
            </p>
          </div>
        </div>
      </div>

      {/* Tableaux par catégorie */}
      {entites.map((categorie, idx) => (
        <div key={idx} className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-500 px-6 py-3">
            <h3 className="text-lg font-semibold text-white">
              {categorie.categorie}
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Caractère
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code HTML
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Exemple d'usage
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categorie.items.map((item, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-3xl font-bold text-green-600">
                        {item.caractere}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="bg-gray-100 px-3 py-1 rounded text-sm font-mono text-blue-600">
                        {item.code}
                      </code>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {item.exemple}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => copyToClipboard(item.code)}
                        className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                          copied === item.code
                            ? 'bg-green-500 text-white'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        {copied === item.code ? '✓ Copié !' : '📋 Copier'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Exemples pratiques */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          📝 Exemples pratiques
        </h3>
        
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded border-l-4 border-green-500">
            <p className="text-sm font-semibold text-gray-700 mb-2">❌ Mauvais (sans entités) :</p>
            <code className="block bg-white p-3 rounded text-sm">
              &lt;p&gt;Commande confirmée pour 45€&lt;/p&gt;
            </code>
          </div>
          
          <div className="bg-gray-50 p-4 rounded border-l-4 border-green-500">
            <p className="text-sm font-semibold text-gray-700 mb-2">✅ Bon (avec entités) :</p>
            <code className="block bg-white p-3 rounded text-sm">
              &lt;p&gt;Commande confirm&amp;eacute;e pour 45&amp;euro;&lt;/p&gt;
            </code>
          </div>
          
          <div className="bg-green-50 p-4 rounded border-l-4 border-green-600">
            <p className="text-sm font-semibold text-green-800 mb-2">🎯 Résultat affiché :</p>
            <p className="text-lg">Commande confirmée pour 45€</p>
          </div>
        </div>
      </div>

      {/* Astuces */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
        <h4 className="font-semibold text-blue-900 mb-2">💡 Astuces :</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Utilisez le bouton "📋 Copier" pour copier rapidement un code</li>
          <li>Dans HeidiSQL, collez directement le code dans votre requête SQL</li>
          <li>Testez toujours vos emails avant envoi !</li>
          <li>Les entités fonctionnent partout : emails, HTML, bases de données</li>
        </ul>
      </div>
    </div>
  );
};

export default MemoEntitesHTML;